import React, { useState } from "react";
import { useModal } from "@/context/ModalContext";
import { requireIdToken } from "../../utils/firebaseAuth";
import { Spinner } from "@jammable/ui-components/Loader";
import axios from "axios";
// "/pure": see CreditCheckoutModal - load Stripe.js on use, not on import.
import { loadStripe } from "@stripe/stripe-js/pure";
import { Clapperboard, Mic, Type } from "lucide-react";
import Image from "next/image";

const MARBLE_SCREENSHOTS = {
  front:
    "https://video-assets.jammable.com/Ig0tfJ6BMuRW3QPF9Gyo4t1sIIA2/6952091318570652b20c20d5bd35899364904_652bd8c7fdb6cd83a53536514097506416165774764.png",
  right:
    "https://video-assets.jammable.com/Ig0tfJ6BMuRW3QPF9Gyo4t1sIIA2/24984770732693425766be265aa534db899467154e_66be288c9c147b1c23e7f78d_66be2baa0406967bff246565_66be2d5a22b3e24ca55b4e0c_66c96cbde4f3eca278bf39b3_659528b4eddb985cddf85b47-3352202780272436743.png",
  left: "https://video-assets.jammable.com/Ig0tfJ6BMuRW3QPF9Gyo4t1sIIA2/-8352111828542680689652b20c20d5bd35899364904_652bd8c7fdb6cd83a5353651-7100330141174501352.png",
};

const PACKS = [
  {
    credits: 10,
    price: "$4.99",
    label: "Starter",
    perCoin: "$0.50",
    coinCount: 1,
  },
  {
    credits: 30,
    price: "$9.99",
    label: "Popular",
    perCoin: "$0.33",
    badge: "BEST VALUE",
    coinCount: 3,
  },
  {
    credits: 100,
    price: "$24.99",
    label: "Pro Pack",
    perCoin: "$0.25",
    badge: "MOST COINS",
    coinCount: 7,
  },
];

const USE_CASES = [
  { icon: Clapperboard, label: "Marble Run Videos" },
  { icon: Mic, label: "Lyric Videos" },
  { icon: Type, label: "All Video Templates" },
];

const CoinStack = ({ count }) => {
  const coins = Array.from({ length: Math.min(count, 7) });
  return (
    <div className="tw-relative tw-flex tw-items-end tw-justify-center tw-h-12 md:tw-h-14">
      {coins.map((_, i) => (
        <img
          key={i}
          src="/coin.png"
          alt=""
          className="tw-absolute tw-w-6 tw-h-6 md:tw-w-8 md:tw-h-8"
          style={{
            bottom: i * 4,
            zIndex: i,
            filter: `brightness(${0.85 + i * (0.15 / Math.max(count - 1, 1))})`,
            transform: `rotate(${(i % 2 === 0 ? -1 : 1) * (i * 3)}deg)`,
          }}
        />
      ))}
    </div>
  );
};

const CreditPackCard = ({ pack, loading, onBuy, featured }) => {
  const isLoading = loading === pack.credits;

  return (
    <div
      className={`tw-relative tw-flex tw-w-full tw-flex-col tw-items-center tw-rounded-[26px] tw-p-3 tw-pt-5 tw-transition md:tw-p-4 md:tw-pt-6 ${
        featured
          ? "tw-bg-jam-purple tw-text-white"
          : "tw-border-2 tw-border-solid tw-border-jam-line tw-bg-white tw-text-jam-ink"
      }`}
    >
      {pack.badge && (
        <div className="tw-absolute tw--top-3 tw-left-0 tw-right-0 tw-z-10 tw-flex tw-justify-center">
          <span
            className={`tw-whitespace-nowrap tw-rounded-full tw-px-2.5 tw-py-1 tw-text-[10px] tw-font-extrabold tw-tracking-wide ${
              featured
                ? "tw-bg-jam-lime tw-text-jam-ink"
                : "tw-bg-jam-ink tw-text-white"
            }`}
          >
            {pack.badge}
          </span>
        </div>
      )}

      <CoinStack count={pack.coinCount} />

      <div className="tw-mt-2 tw-flex tw-items-center tw-gap-1">
        <Image src="/coin.png" alt="Pro Coin" width={16} height={16} />
        <span className="jam-display tw-text-[24px] tw-font-extrabold tw-leading-none tw-tracking-[-0.04em] md:tw-text-[28px]">
          {pack.credits}
        </span>
      </div>
      <div
        className={`tw-mt-0.5 tw-text-[11px] tw-font-bold ${
          featured ? "tw-text-[#E3D3F7]" : "tw-text-jam-muted"
        }`}
      >
        pro coins
      </div>

      <div className="tw-mt-2 tw-text-center">
        <div className="tw-text-lg tw-font-extrabold">{pack.price}</div>
        <div
          className={`tw-text-[11px] tw-font-bold ${
            featured ? "tw-text-[#E3D3F7]" : "tw-text-jam-muted"
          }`}
        >
          {pack.perCoin}/each
        </div>
      </div>

      <button
        onClick={() => onBuy(pack)}
        disabled={loading !== null}
        className={`tw-mt-3 tw-flex tw-h-10 tw-w-full tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-text-[13px] tw-font-extrabold tw-transition active:tw-scale-[0.98] ${
          featured
            ? "tw-bg-jam-lime tw-text-jam-ink hover:tw-brightness-95"
            : "tw-bg-jam-ink tw-text-white hover:tw-bg-jam-deep"
        } disabled:tw-cursor-default disabled:tw-opacity-50`}
      >
        {isLoading ? (
          <Spinner size={4} className="tw-border-3 tw-border-current" />
        ) : (
          "Purchase"
        )}
      </button>
    </div>
  );
};

const CreditPackModal = () => {
  const { openModal } = useModal();
  const [loading, setLoading] = useState(null);

  const handleBuy = async (pack) => {
    setLoading(pack.credits);
    try {
      const token = await requireIdToken();
      const { data } = await axios.post(
        "/api/payment/createCreditPackIntent",
        { packSize: pack.credits },
        { headers: { authorization: token } },
      );

      const stripe = await loadStripe(
        process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
      );
      await stripe.redirectToCheckout({ sessionId: data.sessionId });
    } catch (err) {
      console.error("Failed to create credit pack checkout:", err);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="tw-overflow-hidden tw-bg-white tw-text-jam-ink">
      {/* Hero: lavender panel, display heading, the marble run fan */}
      <div className="tw-m-2 tw-rounded-[28px] tw-bg-jam-lav tw-px-5 tw-pb-5 tw-pt-7 md:tw-m-3 md:tw-rounded-[32px]">
        <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
          <div className="tw-flex tw-items-center tw-gap-2.5">
            <Image src="/coin.png" alt="Pro Coin" width={32} height={32} />
            <h2 className="jam-display tw-m-0 tw-text-[30px] tw-font-extrabold tw-leading-[1.02] tw-tracking-[-0.04em] md:tw-text-[36px]">
              Get Pro Coins
            </h2>
          </div>
          <p className="tw-m-0 tw-mt-2 tw-text-sm tw-font-semibold tw-text-jam-muted md:tw-text-[15px]">
            Each coin creates one shareable video
          </p>
        </div>

        {/* Marble run screenshots - fan layout */}
        <div className="tw-relative tw-mt-5 tw-flex tw-h-[110px] tw-items-center tw-justify-center md:tw-h-[130px]">
          <div
            className="tw-relative tw-flex tw-items-end tw-justify-center"
            style={{ width: 280 }}
          >
            {/* Left screenshot */}
            <img
              src={MARBLE_SCREENSHOTS.left}
              alt=""
              className="tw-absolute tw-rounded-2xl tw-border-[3px] tw-border-solid tw-border-white tw-object-cover"
              style={{
                width: 90,
                height: 100,
                left: 20,
                bottom: 0,
                transform: "rotate(-8deg)",
                zIndex: 1,
              }}
            />
            {/* Center screenshot */}
            <img
              src={MARBLE_SCREENSHOTS.front}
              alt=""
              className="tw-relative tw-rounded-2xl tw-border-[3px] tw-border-solid tw-border-white tw-object-cover"
              style={{
                width: 100,
                height: 115,
                zIndex: 10,
              }}
            />
            {/* Right screenshot */}
            <img
              src={MARBLE_SCREENSHOTS.right}
              alt=""
              className="tw-absolute tw-rounded-2xl tw-border-[3px] tw-border-solid tw-border-white tw-object-cover"
              style={{
                width: 90,
                height: 100,
                right: 20,
                bottom: 0,
                transform: "rotate(8deg)",
                zIndex: 1,
              }}
            />
          </div>
        </div>

        {/* Use cases row */}
        <div className="tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1.5">
          {USE_CASES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-white tw-px-3 tw-py-1.5"
            >
              <Icon
                size={12}
                strokeWidth={2.6}
                className="tw-text-jam-purple"
              />
              <span className="tw-whitespace-nowrap tw-text-[11px] tw-font-extrabold">
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Pack cards */}
      <div className="tw-px-3 tw-pb-6 tw-pt-5 md:tw-px-5">
        <div className="tw-grid tw-grid-cols-3 tw-gap-2.5">
          {PACKS.map((pack) => (
            <CreditPackCard
              key={pack.credits}
              pack={pack}
              loading={loading}
              onBuy={handleBuy}
              featured={pack.credits === 30}
            />
          ))}
        </div>

        <p className="tw-m-0 tw-mt-4 tw-text-center tw-text-xs tw-font-bold tw-text-jam-muted">
          Pro Coins are added instantly and never expire. 1 coin = 1 video.
        </p>
      </div>
    </div>
  );
};

export default CreditPackModal;
